import { Button, Input } from "antd";
import TextArea from "antd/es/input/TextArea";
import { UpdateBusinessBody } from "api/businesses";
import CrawlWebsiteToggle from "components/Common/CrawlWebsiteToggle";
import DescriptionStrengthIndicator from "components/Common/DescriptionStrengthIndicator";
import useGlobalModal from "components/GlobalModals/GlobalModalContext/useGlobalModal";
import ModalWithBorders from "components/ModalWithBorders";
import useOmnibox from "hooks/useOmnibox";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "store";
import { updateBusinessDetails } from "store/business/businessActions";
import { isValidUrl } from "utils/urlUtils";

const ChangeBusinessModal = ({ close }: { close: () => void }) => {
  const dispatch = useAppDispatch();
  const { openGlobalModal } = useGlobalModal();
  const {
    crawlEntireSite,
    inputValue,
    isLoading,
    setCrawlEntireSite,
    setInputValue,
    extractTopics,
  } = useOmnibox({ defaultCrawlEntireSite: false });

  const [content, setContent] = useState("");

  const isButtonDisabled = useMemo(
    () =>
      Boolean(
        inputValue
          ? !isValidUrl(inputValue)
          : !content || (content && content.length < 100)
      ),
    [inputValue, content]
  );

  const handleClose = () => {
    setCrawlEntireSite(true);
    close();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isButtonDisabled) {
      return;
    }

    if (
      event.key === "Enter" &&
      (inputValue ? isValidUrl(inputValue) : content)
    ) {
      changeBusiness();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inputValue, content]);

  const changeBusiness = async () => {
    const res = await extractTopics(content);

    if (!res) {
      return;
    }

    // Destructure only the fields we intend to update
    const {
      logo_url,
      title,
      summary,
      industry,
      niche,
      core_values,
      audience,
      pain_points,
      // Add other relevant fields returned by extractTopics if needed
      // Omit fields not returned or not meant for updateBusinessDetails like keywords, image_urls, topics etc.
    } = res;

    // Prepare the payload for updateBusinessDetails using UpdateBusinessBody
    const updatePayload: Partial<UpdateBusinessBody> = {};

    // Add fields to payload only if they exist in the response, converting undefined to null
    if (title !== undefined) updatePayload.title = title ?? null;
    if (summary !== undefined) updatePayload.summary = summary ?? null;
    if (industry !== undefined) updatePayload.industry = industry ?? null;
    if (niche !== undefined) updatePayload.niche = niche ?? null;
    if (core_values !== undefined)
      updatePayload.core_values = core_values ?? null;
    if (audience !== undefined) updatePayload.audience = audience ?? null;
    if (pain_points !== undefined)
      updatePayload.pain_points = pain_points ?? null;

    // Handle logo specifically (already converting undefined to null)
    updatePayload.logo = logo_url ?? null;

    // Ensure logo_url is string | undefined for modal
    const logoForModal = logo_url ?? undefined;

    // Only dispatch if we have something to update
    if (Object.keys(updatePayload).length > 0) {
      await dispatch(updateBusinessDetails(updatePayload as any));
    } else {
      console.warn(
        "No updatable business details found from extractTopics result."
      );
    }

    // Prepare data for the modal, picking only necessary fields
    // Assuming the modal needs a subset of the original 'res' fields
    const modalBusinessDetails: Record<string, any> = {};
    const modalFields = [
      "id",
      "created_at",
      "title",
      "summary",
      "industry",
      "niche",
      "core_values",
      "audience",
      "pain_points",
    ]; // Add fields required by the modal
    for (const key of modalFields) {
      if (key in res) {
        modalBusinessDetails[key] = (res as any)[key];
      }
    }
    modalBusinessDetails.logoUrl = logoForModal;

    openGlobalModal({
      modalName: "create-business",
      modalData: {
        // Cast to 'any' temporarily if the exact modal type is complex or unknown
        // Ideally, import and use the specific type expected by the 'create-business' modal
        updatedBusinessDetails: modalBusinessDetails as any,
      },
    });
    close();
  };

  return (
    <ModalWithBorders
      centered
      width={window.innerWidth > 768 ? 730 : window.innerWidth - 30}
      className="import-from-website"
      title="Change Business"
      open
      closable={false}
      maskClosable={!isLoading}
      footer={
        <>
          <Button
            className="apply-changes-btn"
            onClick={close}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={isLoading}
            onClick={changeBusiness}
            disabled={isButtonDisabled}
          >
            Change Business
          </Button>
        </>
      }
      onCancel={handleClose}
    >
      <div>
        <label htmlFor="website" className="mb-1">
          Website URL
        </label>
        <Input
          placeholder="Drop a link to your website, blog post, news article, etc."
          onChange={(e) => {
            content && setContent("");
            setInputValue(e.target.value);
          }}
          status={inputValue && !isValidUrl(inputValue) ? "error" : undefined}
          disabled={isLoading}
          value={inputValue}
          id="website"
        />
        {inputValue && !isValidUrl(inputValue) && (
          <span className="warning">Please enter a valid website link</span>
        )}
        {isValidUrl(inputValue) && (
          <CrawlWebsiteToggle
            handleChange={(e) => setCrawlEntireSite(e.target.value)}
            checked={crawlEntireSite}
          />
        )}
      </div>
      <span className="divider">OR</span>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <label htmlFor="new-content">Description</label>
          {content && <DescriptionStrengthIndicator text={content} />}
        </div>
        <TextArea
          id="new-content"
          placeholder="Describe in detail what you want to post about"
          onChange={(e) => {
            inputValue && setInputValue("");
            setContent(e.target.value);
          }}
          disabled={isLoading}
          value={content}
          autoSize={{
            minRows: 2,
            maxRows: 10,
          }}
        />
        {content && content.length < 100 && (
          <span className="warning">
            Description should be at least 100 characters long
          </span>
        )}
      </div>
      <p className="import-from-website__note">
        Warning: This will <span>DELETE</span> your entire current business and
        branding and replace it with what we pull from your website. It will
        regenerate your posts, but won't delete any drafts or scheduled posts.
      </p>
    </ModalWithBorders>
  );
};

export default ChangeBusinessModal;
